'use client';
import React, { useState } from 'react';

import ImageWrapper from '@/components/image-wrapper/Image-wrapper';
import { LandingPageHeader } from '@/components/landing-page-header/landing-page-header';
import { RichText } from '@/components/rich-text/rich-text';
import Button from '@/components/ui/button/button';
import Select from '@/components/ui/select/select';
import {
  SelectOption,
  SelectValue,
} from '@/components/ui/select/select.interface';
import { isBrowser } from '@/helpers/is-browser';
import { ILibraryLanguage } from '@/lib/fragments/libraryLanguage';
import { IMeetingPlacePageLanding } from '@/lib/fragments/meetingPlacePageLanding';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';

interface MeetingPageLandingPageProps {
  contentfulMeetingPlacePageLanding: IMeetingPlacePageLanding;
}

export const MeetingPageLandingPage = ({
  data,
  node_locale,
  preview,
  hrefLangTags,
}: any) => {
  const page = data?.meetingPlacePageLanding;

  const copyRightText =
    '© Ingka Centres Holding B.V. ' + new Date().getFullYear();

  // fetch all meetingplaces with matching contentful_ids
  const meetingplace_contentful_ids = page?.meetingPlaceLinksCollection?.items
    ?.filter(Boolean)
    ?.map((link: any) => link?.contentfulMetaData?.tags?.id);
  // const allMeetingplaceLanguageData = useGetMeetingPlaceLanguages(
  //   meetingplace_contentful_ids,
  // );

  const [selectedMeetingPlaceUrl, setSelectedMeetingPlaceUrl] =
    useState<SelectOption>({ optionText: '', optionValue: '' });

  const [openList, setOpenList] = useState<boolean>(false);

  const selectableLinks: SelectValue[] =
    page?.meetingPlaceLinksCollection?.items
      ?.filter(Boolean)
      ?.map((link: any, i: number) => {
        return {
          id: i.toString(),
          value: {
            optionText: link.dropdownOptionText,
            optionValue: link.pathPrefix,
          },
        };
      });
  const [selectOptions] = useState<SelectValue[]>(selectableLinks);
  const [availableLangs, setAvailableLangs] = useState<string[]>([]);
  const [primaryLang, setPrimaryLang] = useState<string>('');

  const changeHandler = (val: any) => {
    setSelectedMeetingPlaceUrl(val);

    // fetch primary and secondary languages
    const link = page?.meetingPlaceLinksCollection?.items
      ?.filter(Boolean)
      ?.find((l: any) => l.pathPrefix === val.optionValue);
    const contentful_id = link?.metadata?.tags[0]?.contentful_id;
    const matchedMeetingplaces: any = [];
    if (matchedMeetingplaces?.length) {
      const secondaryLanguages =
        matchedMeetingplaces[0]?.languages?.map(
          (lang: ILibraryLanguage) => lang.languageCode,
        ) || [];
      setPrimaryLang(matchedMeetingplaces[0].primaryLanguage.languageCode);
      setAvailableLangs([
        matchedMeetingplaces[0].primaryLanguage.languageCode,
        ...secondaryLanguages,
      ]);
    }
  };
  const handleOnGotoButtonClick = (node_locale: string) => {
    if (isBrowser) {
      const meetingPlacePathPrefix = selectedMeetingPlaceUrl.optionValue;
      let meetingPlaceUrl = '';
      meetingPlaceUrl = `/${meetingPlacePathPrefix}/${node_locale}`;
      // check if the meetingplace have the selected locale
      if (availableLangs.length && availableLangs.includes(node_locale)) {
        meetingPlaceUrl = `/${meetingPlacePathPrefix}/${node_locale}`;
      } else if (primaryLang) {
        meetingPlaceUrl = `/${meetingPlacePathPrefix}/${primaryLang}`;
      }
      window.location.href = meetingPlaceUrl;
    }
  };

  return (
    <>
      {/* ✅ GTM PAGE VIEW */}
      <GtmPageView
        homepage={page}
        language={node_locale}
        pageTitle={page?.name}
        excludeBrowserTitleSuffix
      />
      <main className="relative flex h-[100dvh] flex-col lg:mx-5 lg:mt-5 lg:h-[calc(100dvh-2.5rem)]">
        {page.backgroundImage && (
          <ImageWrapper
            alt={page.backgroundImage?.description}
            url={page?.backgroundImage?.url}
            className="!absolute inset-0 z-0"
            imgClassName="h-full w-full object-cover lg:rounded-xl"
            width={2100}
            height={1400}
          />
        )}
        <LandingPageHeader page={page} hrefLangTags={hrefLangTags || []} />

        <div className="relative z-10 mt-auto px-8 text-center">
          <h1 className="mb-3 font-title text-headingxl text-brand-yellow-1 lg:mt-44 lg:text-headingxxl ">
            {page.name}
          </h1>
          {page.preamble && (
            <div className="mb-8 text-bodymheavy font-bold text-brand-white lg:text-bodyl">
              <RichText richText={page.preamble} />
            </div>
          )}
        </div>
        <div className="relative z-10 mb-12 flex flex-col px-8 lg:mb-auto lg:flex-row lg:justify-center lg:px-0">
          <div className="relative lg:w-[400px]">
            <Select
              values={selectOptions}
              label={page.meetingPlaceLinkSelectorDefaultValue}
              displayLabel={false}
              onChange={changeHandler}
              defaultValue={page.meetingPlaceLinkSelectorDefaultValue}
              value={selectedMeetingPlaceUrl.optionText}
              type="primary"
              setOpenList={setOpenList}
              openList={openList}
              className="border-neutral-grey-200"
            />
          </div>
          <div className="mt-8 lg:ml-6 lg:mt-0">
            <Button
              className="w-full lg:w-auto"
              disabled={
                selectedMeetingPlaceUrl.optionValue === '' ? true : false
              }
              onClick={() => handleOnGotoButtonClick(page.sys.locale)}
              size="regular"
            >
              {page.buttonText}
            </Button>
          </div>
        </div>
        <div className="relative z-10 mb-5 ml-5 text-bodys text-brand-white lg:mb-9 lg:ml-8">
          {copyRightText}
        </div>
      </main>
    </>
  );
};

export default MeetingPageLandingPage;

// export const query = graphql`
//   query MeetingPlacePageLandingQuery($id: String!, $languages: [String!]) {
//     contentfulMeetingPlacePageLanding(
//       id: { eq: $id }
//       node_locale: { in: $languages }
//     ) {
//       ...MeetingPlacePageLanding
//     }
//   }
// `;
